/* eslint-disable react-refresh/only-export-components */
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  createElement,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollToPlugin);

type Listener<T> = (value: T) => void;

export class MotionValue<T> {
  private value: T;
  private readonly listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.value = initial;
  }

  get() {
    return this.value;
  }

  set(value: T) {
    this.value = value;
    this.listeners.forEach((listener) => listener(value));
  }

  on(listener: Listener<T>) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }
}

export type ProgressValue = MotionValue<number>;

export function useMotionValue<T>(initial: T) {
  const [value] = useState(() => new MotionValue(initial));
  return value;
}

function interpolate<T>(value: number, input: readonly number[], output: readonly T[]): T {
  let index = input.findIndex((point) => value <= point) - 1;
  if (index < 0) index = value <= input[0] ? 0 : input.length - 2;
  index = Math.min(index, input.length - 2);
  const start = input[index];
  const end = input[index + 1];
  const ratio = Math.max(0, Math.min(1, (value - start) / Math.max(Number.EPSILON, end - start)));
  const from = output[index];
  const to = output[index + 1];
  if (typeof from === "number" && typeof to === "number") {
    return (from + (to - from) * ratio) as T;
  }
  if (typeof from === "string" && typeof to === "string") {
    const fromNumber = Number.parseFloat(from);
    const toNumber = Number.parseFloat(to);
    if (Number.isFinite(fromNumber) && Number.isFinite(toNumber)) {
      return from.replace(String(fromNumber), String(fromNumber + (toNumber - fromNumber) * ratio)) as T;
    }
    const fromMatch = from.match(/-?\d+(?:\.\d+)?/);
    const toMatch = to.match(/-?\d+(?:\.\d+)?/);
    if (fromMatch && toMatch) {
      const mixed = Number(fromMatch[0]) + (Number(toMatch[0]) - Number(fromMatch[0])) * ratio;
      return from.replace(fromMatch[0], String(mixed)) as T;
    }
  }
  return (ratio < 0.5 ? from : to) as T;
}

export function useTransform<T>(source: MotionValue<number>, transform: (value: number) => T): MotionValue<T>;
export function useTransform<T>(source: MotionValue<number>, input: readonly number[], output: readonly T[]): MotionValue<T>;
export function useTransform<T>(source: MotionValue<number>, inputOrTransform: readonly number[] | ((value: number) => T), output?: readonly T[]) {
  const calculate = useMemo(() => typeof inputOrTransform === "function"
    ? inputOrTransform
    : (value: number) => interpolate(value, inputOrTransform, output ?? []), [inputOrTransform, output]);
  const [derived] = useState(() => new MotionValue(calculate(source.get())));

  useEffect(() => source.on((value) => derived.set(calculate(value))), [calculate, derived, source]);
  return derived;
}

export function useMotionValueEvent<T>(value: MotionValue<T>, _event: "change", callback: Listener<T>) {
  useEffect(() => value.on(callback), [callback, value]);
}

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

interface ScrollOptions {
  target?: RefObject<Element | null>;
  offset?: readonly [string, string];
}

function toTriggerPoint(value: string | undefined, fallback: string) {
  return value?.replace("start", "top").replace("end", "bottom") ?? fallback;
}

export function useScroll(options: ScrollOptions = {}) {
  const scrollY = useMotionValue(0);
  const scrollYProgress = useMotionValue(0);

  useGSAP(() => {
    if (options.target?.current) {
      const trigger = ScrollTrigger.create({
        trigger: options.target.current,
        start: toTriggerPoint(options.offset?.[0], "top bottom"),
        end: toTriggerPoint(options.offset?.[1], "bottom top"),
        onUpdate: (self) => scrollYProgress.set(self.progress),
      });
      scrollYProgress.set(trigger.progress);
      return () => trigger.kill();
    }

    const update = () => {
      scrollY.set(window.scrollY);
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      scrollYProgress.set(window.scrollY / max);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, { dependencies: [options.target, options.offset] });

  return { scrollY, scrollYProgress };
}

interface AnimateOptions {
  duration?: number;
  ease?: string | readonly number[];
  onUpdate?: (value: number) => void;
  onComplete?: () => void;
}

export function animate(value: MotionValue<number>, destination: number, options: AnimateOptions = {}) {
  const state = { value: value.get() };
  const tween = gsap.to(state, {
    value: destination,
    duration: options.duration,
    ease: typeof options.ease === "string" ? options.ease : "power3.out",
    onUpdate: () => {
      value.set(state.value);
      options.onUpdate?.(state.value);
    },
    onComplete: options.onComplete,
  });
  return { stop: () => tween.kill() };
}

type MotionStyle = Record<string, unknown>;
type MotionProps = {
  [key: string]: unknown;
  children?: ReactNode;
  className?: string;
  style?: MotionStyle;
  initial?: MotionStyle | false;
  animate?: MotionStyle;
  whileInView?: MotionStyle;
  transition?: { duration?: number; delay?: number; ease?: string | readonly number[] };
  viewport?: { once?: boolean; amount?: number };
};

function createMotion(tag: keyof React.JSX.IntrinsicElements) {
  return function MotionElement(props: MotionProps) {
    const localRef = useRef<Element>(null);
    const initial = props.initial as MotionStyle | false | undefined;
    const animateTo = props.animate as MotionStyle | undefined;
    const whileInView = props.whileInView as MotionStyle | undefined;
    const transition = props.transition as MotionProps["transition"];
    const viewport = props.viewport as MotionProps["viewport"];
    const style = (props.style as MotionStyle | undefined) ?? {};
    const domProps = { ...props };
    delete domProps.initial;
    delete domProps.animate;
    delete domProps.whileInView;
    delete domProps.transition;
    delete domProps.viewport;
    delete domProps.layoutId;
    const staticStyle = Object.fromEntries(Object.entries(style).filter(([, value]) => !(value instanceof MotionValue)));

    useGSAP(() => {
      const node = localRef.current;
      if (!node) return;
      const subscriptions = Object.entries(style).flatMap(([property, value]) => value instanceof MotionValue
        ? [value.on((next) => gsap.set(node, { [property]: next }))]
        : []);
      gsap.set(node, Object.fromEntries(Object.entries(style).map(([property, value]) => [property, value instanceof MotionValue ? value.get() : value])));
      const vars = {
        duration: transition?.duration ?? 0.5,
        delay: transition?.delay ?? 0,
        ease: typeof transition?.ease === "string" ? transition.ease : "power3.out",
      };
      if (initial !== false && animateTo) gsap.fromTo(node, initial ?? {}, { ...animateTo, ...vars });
      else if (animateTo) gsap.to(node, { ...animateTo, ...vars });
      if (whileInView) {
        gsap.fromTo(node, initial === false ? {} : initial ?? {}, {
          ...whileInView,
          ...vars,
          scrollTrigger: { trigger: node, start: `top ${100 - Math.round((viewport?.amount ?? 0.15) * 100)}%`, once: viewport?.once },
        });
      }
      return () => subscriptions.forEach((unsubscribe) => unsubscribe());
    }, { dependencies: [animateTo, initial, style, transition, viewport, whileInView] });

    return createElement(tag, {
      ...domProps,
      style: staticStyle,
      ref: localRef,
    });
  };
}

export const motion = {
  div: createMotion("div"),
  span: createMotion("span"),
  i: createMotion("i"),
  path: createMotion("path"),
  circle: createMotion("circle"),
  g: createMotion("g"),
};

export function AnimatePresence({ children }: { children?: ReactNode; mode?: string; initial?: boolean }) {
  return <Fragment>{children}</Fragment>;
}
